import React, { useRef, useEffect } from 'react'
import { connect } from "react-redux";
import workspace from "../canvas/core/workspace";
import { changeValuePopupInterface } from "../../interfaces/sqftInterfaces";
import useLongPress from "../../hocs/useLongPress";

const ChangeValuePopupWindow = (props) => {
  const {
    id,
    min,
    max,
    confirmCallback,
    increaseCallback,
    decreaseCallback,
    inputChangeCallback,
    value,
    position,
    title,
    shouldHideCloseButton,
    autoFocus = false,
    isInline = false
  } = props;

  const inputRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const hasSelectedRef = useRef(false);

  const onIncreaseLongPress = () => {
    increaseCallback(id);
  };

  const onIncreaseClick = (arg) => {
    increaseCallback(arg);
  };

  const onDecreaseLongPress = () => {
    decreaseCallback(id);
  };

  const onDecreaseClick = (arg) => {
    decreaseCallback(arg);
  };

  // Restore original semantics: inputChangeCallback(e)
  const onInputChange = (e) => {
    inputChangeCallback(e);
  };

  const defaultOptions = {
    shouldPreventDefault: true,
    delay: 500,
  };

  const increaseLongPressEvent = useLongPress(onIncreaseLongPress, onIncreaseClick, defaultOptions);
  const decreaseLongPressEvent = useLongPress(onDecreaseLongPress, onDecreaseClick, defaultOptions);

  const handleMouseLeave = () => {
    blurTimeoutRef.current = setTimeout(() => {
      if (inputRef.current && typeof inputRef.current.blur === 'function') {
        inputRef.current.blur();
      }
    }, 200);
  };

  const handleMouseEnter = () => {
    clearTimeout(blurTimeoutRef.current);
  };

  // Select on first focus, whether requested by the caller or initiated by the user.
  const handleFocus = () => {
    if (inputRef.current && !hasSelectedRef.current) {
      hasSelectedRef.current = true;
      inputRef.current.select();
    }
  };

  const handleKeyDown = (e) => {
    if (props.onKeyDown) {
      props.onKeyDown(e);
      return;
    }
    if (!autoFocus) {
      e.stopPropagation();
    }
  };

  useEffect(() => {
    hasSelectedRef.current = false;
    const timerId = autoFocus ? setTimeout(() => {
      const input = inputRef.current;
      if (!input || hasSelectedRef.current) {
        return;
      }
      input.focus({ preventScroll: true });
      input.select();
      hasSelectedRef.current = true;
    }, 0) : null;

    return () => {
      clearTimeout(timerId);
      clearTimeout(blurTimeoutRef.current);
    };
  }, [id, autoFocus]);

  const valueInput = (
    <input
      id={"change-value-" + id}
      type="text"
      className="ui-input tooltip-angle-nav-input"
      min={min}
      max={max}
      value={value}
      aria-invalid={props.inputError ? true : undefined}
      title={props.inputError || undefined}
      onChange={onInputChange}
      onFocus={handleFocus}
      onKeyDown={handleKeyDown}
      ref={inputRef}
    />
  );

  if (isInline) {
    return (
      <div className="drawing-shortcuts__parameter" onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseEnter}>
        <label className="drawing-shortcuts__parameter-label" htmlFor={"change-value-" + id}>{title}</label>
        <div className="drawing-shortcuts__stepper">
          <button type="button" className="drawing-shortcuts__step" aria-label={workspace.intl.formatMessage({id: "sqft.drawing.decrease", defaultMessage: "Decrease value"})} {...decreaseLongPressEvent}>−</button>
          {valueInput}
          <button type="button" className="drawing-shortcuts__step" aria-label={workspace.intl.formatMessage({id: "sqft.drawing.increase", defaultMessage: "Increase value"})} {...increaseLongPressEvent}>+</button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={"change-value-popup tooltip tooltip-angle change-value-popup-" + (id)}
      style={{ left: position.left + 'px', top: position.top + 'px' }}
      onMouseLeave={handleMouseLeave}
      onMouseEnter={handleMouseEnter}
    >
      <div className="tooltip-angle-nav">
        {title ? (
          <div className="tooltip-angle-header">
            {title}
          </div>
        ) : null}

        {valueInput}

        <div
          className="tooltip-angle-nav-arrow tooltip-angle-nav-arrow-up"
          {...increaseLongPressEvent}
        >
          <svg className="triangle">
            <path d="M 22 8 L 40 30 L 5 30 z" stroke="#ffffff" strokeLinejoin="round" strokeWidth="8px"/>
            <path d="M 22 13 L 35 30 L 9 30 z" fill="#4A90E2" stroke="#4A90E2" strokeLinejoin="round" strokeWidth="8px"/>
          </svg>
        </div>

        <div
          className="tooltip-angle-nav-arrow tooltip-angle-nav-arrow-down"
          {...decreaseLongPressEvent}
        >
          <svg className="triangle">
            <path d="M 22 8 L 40 30 L 5 30 z" stroke="#ffffff" strokeLinejoin="round" strokeWidth="8px"/>
            <path d="M 22 13 L 35 30 L 9 30 z" fill="#4A90E2" stroke="#4A90E2" strokeLinejoin="round" strokeWidth="8px"/>
          </svg>
        </div>
      </div>

      {
        shouldHideCloseButton
          ? null
          : (
            <button
              className="ui-button10 ui-button10--green tooltip-angle-btn"
              onClick={() => confirmCallback()}
            >
              {workspace.intl.formatMessage({ id: "sqft.contextmenu.angleTooltipClose" })}
            </button>
          )
      }
    </div>
  );
};

ChangeValuePopupWindow.propTypes = changeValuePopupInterface;

const mapStateToProps = (store) => ({
  activeButton: store.sqftMenuState.activeButton,
});

export default connect(mapStateToProps)(ChangeValuePopupWindow);
